import { DirectiveBinding } from 'vue/types/options'

interface CurrencyOptions {
  prefix?: string
  suffix?: string
  thousands?: string
  decimal?: string
  precision?: number
}

// Format number as currency
function formatCurrency(value: number | string, options: CurrencyOptions): string {
  const { prefix = '', suffix = '', thousands = '.', decimal = ',', precision = 2 } = options

  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numValue)) return ''

  const negative = numValue < 0 ? '-' : ''
  const absValue = Math.abs(numValue)

  const fixed = absValue.toFixed(precision)
  const [integerPart, decimalPart] = fixed.split('.')

  // Add thousand separators
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)

  const formattedValue = decimalPart ? `${formattedInteger}${decimal}${decimalPart}` : formattedInteger

  return `${prefix}${negative}${formattedValue}${suffix}`
}

// Parse formatted currency back to number
function parseCurrency(formattedValue: string, options: CurrencyOptions): number {
  const { thousands = '.', decimal = ',' } = options

  // Remove all non-numeric characters except decimal separator and minus
  const cleanValue = formattedValue
    .replace(new RegExp(`\\${thousands}`, 'g'), '')
    .replace(new RegExp(`\\${decimal}`), '.')
    .replace(/[^\d.-]/g, '')

  const numValue = parseFloat(cleanValue)
  return isNaN(numValue) ? 0 : numValue
}

// Handle input in right-to-left manner
function handleRightToLeftInput(input: HTMLInputElement, options: CurrencyOptions) {
  const { precision = 2 } = options

  // Get only numeric characters
  const numbers = input.value.replace(/\D/g, '')

  if (!numbers) {
    input.value = ''
    return 0
  }

  // Convert to number with decimal places
  const numValue = parseInt(numbers) / Math.pow(10, precision)

  // Format and set the value
  input.value = formatCurrency(numValue, options)

  // Move cursor to end
  setTimeout(() => {
    input.setSelectionRange(input.value.length, input.value.length)
  }, 0)

  return numValue
}

export const currencyInput = {
  bind(el: HTMLElement, binding: DirectiveBinding) {
    const input = el.querySelector('input') as HTMLInputElement
    if (!input) return

    const options: CurrencyOptions = binding.value || {}

    // Store original value getter/setter
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')
    const originalSet = descriptor?.set

    // Flag to prevent infinite loops
    let isUpdating = false

    // Handle input events for right-to-left typing
    const handleInput = () => {
      if (isUpdating) return
      isUpdating = true

      const numValue = handleRightToLeftInput(input, options)

      // Emit the numeric value
      const vueComponent = (el as any).__vue__
      if (vueComponent) {
        vueComponent.$emit('input', numValue)
        vueComponent.$emit('change', numValue)
      }

      isUpdating = false
    }

    // Handle blur to ensure proper formatting
    const handleBlur = () => {
      if (isUpdating) return
      isUpdating = true

      const numValue = parseCurrency(input.value, options)
      input.value = formatCurrency(numValue, options)

      isUpdating = false
    }

    // Handle focus - select all for easy replacement
    const handleFocus = () => {
      setTimeout(() => {
        input.setSelectionRange(0, input.value.length)
      }, 0)
    }

    // Override value setter to format on programmatic updates
    if (originalSet) {
      Object.defineProperty(input, 'value', {
        get() {
          return descriptor.get?.call(this) || ''
        },
        set(newValue: string) {
          if (isUpdating) {
            originalSet.call(this, newValue)
            return
          }

          isUpdating = true
          // Distinguish between Brazilian formatted strings (with comma) and plain numeric strings
          let numValue: number
          if (typeof newValue === 'string') {
            // If contains comma (Brazilian decimal separator), it's formatted - use parseCurrency
            // Otherwise, it's a plain number string (from number.toString()) - use parseFloat
            numValue = newValue.includes(',') ? parseCurrency(newValue, options) : parseFloat(newValue)
          } else {
            numValue = newValue
          }
          const formatted = formatCurrency(numValue || 0, options)
          originalSet.call(this, formatted)
          isUpdating = false
        }
      })
    }

    // Store handlers on element for cleanup
    ;(el as any)._currencyHandlers = {
      input: handleInput,
      blur: handleBlur,
      focus: handleFocus
    }

    // Add event listeners
    input.addEventListener('input', handleInput)
    input.addEventListener('blur', handleBlur)
    input.addEventListener('focus', handleFocus)

    // Initial format
    if (input.value) {
      const numValue = parseCurrency(input.value, options)
      input.value = formatCurrency(numValue, options)
    }
  },

  update(el: HTMLElement, binding: DirectiveBinding) {
    const input = el.querySelector('input') as HTMLInputElement
    if (!input) return

    const options: CurrencyOptions = binding.value || {}
    const handlers = (el as any)._currencyHandlers

    if (binding.oldValue !== binding.value && handlers) {
      // Update with new options
      const numValue = parseCurrency(input.value, binding.oldValue || {})
      input.value = formatCurrency(numValue, options)
    }
  },

  unbind(el: HTMLElement) {
    const input = el.querySelector('input') as HTMLInputElement
    if (!input) return

    const handlers = (el as any)._currencyHandlers
    if (handlers) {
      input.removeEventListener('input', handlers.input)
      input.removeEventListener('blur', handlers.blur)
      input.removeEventListener('focus', handlers.focus)
      delete (el as any)._currencyHandlers
    }
  }
}
